package one.healthium.health_advice.ui.message

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import loco.PCOS_TODAY_HEALTH_ADVICE_EMPTY_TEXT
import loco.Res
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.white
import org.jetbrains.compose.resources.stringResource
import androidx.compose.ui.tooling.preview.Preview
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun HealthAdviceMessage(
    modifier: Modifier = Modifier,
    bringIntoViewRequester: BringIntoViewRequester,
    onLogSymptomsClicked: () -> Unit,
    style: TextStyle? = null,
    color: Color? = null,
    textAlign: TextAlign? = null,
    lineHeight: TextUnit? = null,
) {
    val viewModel = koinViewModel<HealthAdviceMessageViewModel>()
    val state by viewModel.state.collectAsState()

    HealthAdviceMessageScreenContent(
        modifier = modifier,
        state = state,
        bringIntoViewRequester = bringIntoViewRequester,
        onLogSymptomsClicked = onLogSymptomsClicked,
        style = style,
        color = color,
        textAlign = textAlign,
        lineHeight = lineHeight,
    )
}

@Composable
private fun HealthAdviceMessageScreenContent(
    modifier: Modifier = Modifier,
    state: HealthAdviceMessageUiState,
    bringIntoViewRequester: BringIntoViewRequester,
    onLogSymptomsClicked: () -> Unit,
    style: TextStyle? = null,
    color: Color? = null,
    textAlign: TextAlign? = null,
    lineHeight: TextUnit? = null,
) {
    val message = state.message
    if (message == null) {
        // If there is no health log message, we don't show the advice text.
        return
    }

    val (healthLogMessage, isEmpty) = if (message.isEmpty()) {
        stringResource(Res.string.PCOS_TODAY_HEALTH_ADVICE_EMPTY_TEXT) to true
    } else {
        message to false
    }

    var currentText by remember { mutableStateOf(healthLogMessage) }
    val alpha = remember { Animatable(1f) }

    // This effect runs whenever the input 'text' changes.
    LaunchedEffect(healthLogMessage) {
        if (currentText == healthLogMessage) return@LaunchedEffect // skip animation when transition from empty to non-empty

        bringIntoViewRequester.bringIntoView()
        alpha.animateTo(0f, animationSpec = tween(300))

        currentText = healthLogMessage

        alpha.animateTo(1f, animationSpec = tween(300))
    }

    AppText(
        modifier = modifier
            .let {
                if (isEmpty) {
                    it.clickable(onClick = onLogSymptomsClicked)
                } else {
                    it
                }
            }
            .padding(horizontal = 24.dp, vertical = 16.dp)
            .alpha(alpha.value),
        text = currentText,
        textAlign = textAlign ?: TextAlign.Center,
        style = style ?: AppTheme.textStyles.secondary_14_400,
        color = color ?: white,
        lineHeight = lineHeight ?: 20.sp,
    )
}

@Preview(showBackground = true)
@Composable
fun HealthAdviceMessageScreenPreview() {
    AppTheme {
        HealthAdviceMessageScreenContent(
            state = HealthAdviceMessageUiState(message = "Perimenopause can intensify period symptoms. You might feel: cramps, bloating"),
            bringIntoViewRequester = BringIntoViewRequester(),
            onLogSymptomsClicked = {},
        )
    }
}
